import { cn } from '@heidi/utils';
import type { ReactNode } from 'react';

type TableCellProps = {
  children?: ReactNode;
  [key: string]: unknown;
};

/**
 * Shared `table`/`thead`/`th`/`td` renderers for streamed assistant prose.
 *
 * Every surface that renders a markdown table builds it from here — the prose
 * map in `response-prose.tsx` (Evidence and Work) and the desktop overlay. The
 * wrapper always clamps width, so a wide table scrolls inside it instead of
 * resizing its container. Each caller differs on exactly one thing, so each of
 * those stays a parameter rather than being folded in: the measured surfaces
 * (Work timeline, desktop overlay) keep headers on one line, the Evidence panel
 * tints body cells to separate them from the page background, and the desktop
 * overlay is a floating HUD whose type sits below the prose base, so it sets
 * `cellTextClass` rather than taking the density's body size.
 */
export function createTableMdComponents({
  wrapperClassName,
  headerNoWrap = false,
  tintedCell = false,
  cellTextClass = 'text-base',
}: {
  wrapperClassName: string;
  headerNoWrap?: boolean;
  /** Tint body cells so a table separates from the page background. */
  tintedCell?: boolean;
  cellTextClass?: string;
}) {
  return {
    table: ({ children }: TableCellProps) => (
      <div className={wrapperClassName}>
        <div className="scrollbar-fade-x min-w-0 overflow-x-auto">
          <table className={cn('w-full', cellTextClass)}>{children}</table>
        </div>
      </div>
    ),
    thead: ({ children }: TableCellProps) => (
      <thead className="bg-surface-hover">{children}</thead>
    ),
    // `dir="auto"` per cell: unlocalized (usually English) cell content keeps
    // LTR bidi resolution in an RTL locale, so threshold values like
    // `≥126 mg/dL` don't get their comparison sign mirrored/relocated.
    // `text-start` (not `text-left`) so header alignment follows each cell's
    // resolved direction.
    th: ({ children }: TableCellProps) => (
      <th
        dir="auto"
        className={cn(
          'border-b border-r px-3 py-2 text-start font-semibold text-text-secondary last:border-r-0',
          cellTextClass,
          headerNoWrap && 'whitespace-nowrap'
        )}
      >
        {children}
      </th>
    ),
    td: ({ children }: TableCellProps) => (
      <td
        dir="auto"
        className={cn(
          'border-b border-r px-3 py-2 font-normal last:border-r-0 [tr:last-child_&]:border-b-0',
          cellTextClass,
          tintedCell && 'bg-surface-hover/20'
        )}
      >
        {children}
      </td>
    ),
  };
}
